// apps/share-web/src/chart/lyrics-text.ts
//
// Слова песни простым текстом — для слайдов проектора
// (B-261002-web-words-link-text-not-copyable).
//
// Команда, которая выводит слова на экран, забирает их со страницы слов в свою
// программу (ProPresenter, EasyWorship…). Решение Сергея 06.10: ТОЛЬКО слова
// одной песни, блоки через пустую строку — по пустой строке эти программы и
// режут текст на слайды. Без аккордов, без названий секций, без заметок.
//
// Текст собирается С ЛИСТА, который уже нарисовал `renderChart`, а не из
// исходника песни: лист — это ровно то, что видит человек, и разбор ChordPro
// второй раз (с его повторами, табулатурой, директивами) дал бы вторую правду
// о том же тексте. Строка листа — `.cw-ln`; слово в ней разрезано по слогам под
// аккорды (`.cw-wd > .cw-u > .cw-ch + .cw-tx`), пробелы — `.cw-sp`.
//
// Почему не просто выделение браузера: единицы слова свёрстаны колонкой
// «аккорд над слогом», и браузер копирует каждое слово отдельной строкой
// («ravel / donta / miren…»). Поэтому и копирование ВЫДЕЛЕНИЯ подменяется тем
// же сборщиком (`installLyricsCopy`): строки целиком, блоки через пустую строку.

const isLine = (n: Element): boolean => n.classList.contains('cw-ln');

// Слог «аккорд без слога» (`.is-bare`) несёт в `.cw-tx` пробел-распорку для
// вёрстки: «br[F][G]una» без этого исключения копировалось бы как «br una».
const LYRIC_PARTS = '.cw-u:not(.is-bare) .cw-tx, .cw-sp';

/**
 * Часть текстового узла внутри выделения. Сравниваются ГРАНИЦЫ, а не «узел
 * задет»: протяжка мышью до начала следующей строки кончается на её первом
 * слоге со смещением 0 — строка задета, но не выделена (ревью: копировалась
 * лишняя строка). Границы-элементы (`<span>@0`) отрабатывают так же.
 */
function clip(node: Text, range: Range): string {
  if (!range.intersectsNode(node)) return '';
  const own = node.ownerDocument.createRange();
  own.selectNodeContents(node);
  if (range.compareBoundaryPoints(Range.START_TO_START, own) > 0) own.setStart(range.startContainer, range.startOffset);
  if (range.compareBoundaryPoints(Range.END_TO_END, own) < 0) own.setEnd(range.endContainer, range.endOffset);
  return own.toString();
}

/**
 * Слова одной строки листа: слоги и пробелы по порядку, без аккордов. С
 * `range` — только выделенная часть строки.
 */
export function lineLyrics(line: Element, range?: Range | null): string {
  let s = '';
  for (const part of line.querySelectorAll(LYRIC_PARTS)) {
    if (!range) { s += part.textContent ?? ''; continue; }
    for (const child of part.childNodes) {
      if (child.nodeType === 3) s += clip(child as Text, range);
    }
  }
  return s.replace(/\s+/g, ' ').trim();
}

/**
 * Слова листа для слайдов. Без `range` — вся песня: повтор припева (`{chorus}`,
 * блок `.is-repeat`) не дублируется — слайд припева один, оператор его повторит;
 * табулатура (`.is-verbatim`) — не слова. С `range` — только строки, которых
 * касается выделение (повтор тоже, если человек выделил именно его).
 */
export function sheetLyrics(root: HTMLElement, range?: Range | null): string {
  const blocks: string[] = [];
  for (const blk of root.querySelectorAll('.cw-blk')) {
    if (blk.classList.contains('is-verbatim')) continue;
    if (!range && blk.classList.contains('is-repeat')) continue;
    const paragraphs: string[][] = [[]];
    for (const child of blk.children) {
      if (child.classList.contains('cw-gap')) {
        if (paragraphs[paragraphs.length - 1].length) paragraphs.push([]);
        continue;
      }
      if (!isLine(child)) continue;
      if (range && !range.intersectsNode(child)) continue;
      const text = lineLyrics(child, range);
      if (text) paragraphs[paragraphs.length - 1].push(text);
    }
    const body = paragraphs.filter((p) => p.length).map((p) => p.join('\n')).join('\n\n');
    if (body) blocks.push(body);
  }
  return blocks.join('\n\n');
}

const installed = new WeakSet<Document>();

/**
 * Копирование выделения на листе слов — тем же сборщиком, что и кнопка.
 * Один слушатель на документ (лист перерисовывается на каждую песню и каждую
 * настройку — слушатель на листе копился бы). Выделение вне листов или без
 * слов — браузер копирует сам, как обычно.
 */
export function installLyricsCopy(doc: Document = document): void {
  if (installed.has(doc)) return;
  installed.add(doc);
  doc.addEventListener('copy', (e: ClipboardEvent) => {
    const sel = doc.getSelection();
    if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return;
    const range = sel.getRangeAt(0);
    const parts: string[] = [];
    for (const sheet of doc.querySelectorAll('.cw-sheet')) {
      if (!range.intersectsNode(sheet)) continue;
      const text = sheetLyrics(sheet as HTMLElement, range);
      if (text) parts.push(text);
    }
    if (!parts.length || !e.clipboardData) return;
    e.clipboardData.setData('text/plain', parts.join('\n\n'));
    e.preventDefault();
  });
}
